import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Brain, Check, Loader2, ArrowRight, Languages } from "lucide-react";

const GRADES = [
  { g: "again", label: "Again", hint: "blanked", cls: "border-rose-500/40 text-rose-300 hover:bg-rose-500/[0.08]" },
  { g: "hard", label: "Hard", hint: "struggled", cls: "border-amber-500/40 text-amber-300 hover:bg-amber-500/[0.08]" },
  { g: "good", label: "Good", hint: "got it", cls: "border-white/20 text-white hover:bg-white/[0.06]" },
  { g: "easy", label: "Easy", hint: "instant", cls: "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/[0.08]" },
];

const Review = () => {
  const [state, setState] = useState(null);
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [showEn, setShowEn] = useState(true);

  const load = async () => {
    const { data } = await api.get("/review/today", { params: { limit: 30 } });
    setState(data);
    setI(0);
    setFlipped(false);
  };
  useEffect(() => { load(); }, []);

  const grade = async (g) => {
    const card = state.cards[i];
    if (!card) return;
    setBusy(true);
    try {
      const { data } = await api.post("/review/grade", { card_id: card.id, grade: g });
      setDone((d) => d + 1);
      setState((s) => ({ ...s, streak: data.streak, due_count: data.due_count }));
      if (i + 1 >= state.cards.length) {
        const { data: fresh } = await api.get("/review/today", { params: { limit: 30 } });
        setState(fresh); setI(0); setFlipped(false);
      } else {
        setI(i + 1); setFlipped(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!state) return <div className="p-10 text-neutral-500 text-[0.86rem]">Loading…</div>;

  const card = state.cards?.[i];
  const hasEn = !!(card?.front_en || card?.back_en);

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="review-page">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono mb-3">/ daily recall</div>
          <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">
            Five minutes. Every day.
          </h1>
          <p className="text-neutral-500 mt-3 text-[0.92rem]">
            Built from the lessons you actually recorded — surfaced right before you&apos;d forget them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="card-panel px-4 py-2.5 flex items-center gap-2.5" data-testid="streak-tile">
            <Flame size={16} className={state.streak > 0 ? "text-amber-400" : "text-neutral-600"} />
            <div>
              <div className="label-mono text-[0.6rem] mb-0.5">streak</div>
              <div className="text-sm tabular text-white">{state.streak} day{state.streak === 1 ? "" : "s"}</div>
            </div>
          </div>
          <div className="card-panel px-4 py-2.5">
            <div className="label-mono text-[0.6rem] mb-0.5">due now</div>
            <div className="text-sm tabular text-white" data-testid="due-count">{state.due_count}</div>
          </div>
          <div className="card-panel px-4 py-2.5">
            <div className="label-mono text-[0.6rem] mb-0.5">today</div>
            <div className="text-sm tabular text-white" data-testid="reviewed-today">{state.reviewed_today + done}</div>
          </div>
        </div>
      </header>

      {state.total_cards === 0 && (
        <div className="glass-strong p-10 text-center" data-testid="review-empty-all">
          <Brain size={26} className="text-neutral-500 mx-auto mb-5" />
          <div className="font-display text-xl tightest font-semibold mb-3">No cards yet</div>
          <p className="text-neutral-500 text-[0.9rem] mb-7 max-w-md mx-auto leading-relaxed">
            Record a lesson and generate flashcards — every card you make is automatically scheduled here,
            so revision happens on its own.
          </p>
          <Link to="/dashboard"><button className="btn-primary" data-testid="review-goto-dashboard">Record a lesson <ArrowRight size={13}/></button></Link>
        </div>
      )}

      {state.total_cards > 0 && !card && (
        <div className="glass-strong p-10 text-center" data-testid="review-done">
          <div className="w-12 h-12 mx-auto mb-5 border border-emerald-500/40 bg-emerald-500/[0.08] flex items-center justify-center rounded-[2px]">
            <Check size={20} className="text-emerald-400" />
          </div>
          <div className="font-display text-2xl tightest font-semibold mb-3">Recall cleared.</div>
          <p className="text-neutral-500 text-[0.9rem] mb-2">
            {state.reviewed_today + done} cards reviewed today · {state.streak}-day streak.
          </p>
          <p className="text-neutral-600 text-[0.82rem]">Come back tomorrow — we&apos;ll queue the ones you&apos;re about to forget.</p>
        </div>
      )}

      {card && (
        <div data-testid="review-card-wrap">
          <div className="flex items-center justify-between mb-3">
            <div className="label-mono">{card.subject} · {i + 1} of {state.cards.length}</div>
            {hasEn && (
              <button onClick={() => setShowEn(!showEn)} className="inline-flex items-center gap-1.5 text-[0.75rem] text-neutral-500 hover:text-white transition-colors" data-testid="review-toggle-en">
                <Languages size={12} className="text-amber-400" /> {showEn ? "Hide English" : "Show English"}
              </button>
            )}
          </div>

          <button
            onClick={() => setFlipped((f) => !f)}
            className="w-full glass-strong p-10 min-h-[15rem] flex flex-col items-center justify-center gap-4 text-center transition-colors hover:border-white/20"
            data-testid="review-card"
          >
            {!flipped ? (
              <>
                <div className="font-display text-2xl md:text-3xl tightest font-semibold i18n-text">{card.front}</div>
                {showEn && card.front_en && card.front_en !== card.front && (
                  <div className="text-neutral-500 text-[0.88rem] max-w-lg">{card.front_en}</div>
                )}
                <div className="label-mono mt-4 text-neutral-600">tap to reveal</div>
              </>
            ) : (
              <>
                <div className="text-amber-400 text-xl md:text-2xl font-medium i18n-text" data-testid="review-answer">{card.back}</div>
                {showEn && card.back_en && card.back_en !== card.back && (
                  <div className="text-neutral-500 text-[0.88rem] max-w-lg">{card.back_en}</div>
                )}
              </>
            )}
          </button>

          {flipped && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4" data-testid="grade-row">
              {GRADES.map((g) => (
                <button
                  key={g.g}
                  onClick={() => grade(g.g)}
                  disabled={busy}
                  data-testid={`grade-${g.g}`}
                  className={`border rounded-[2px] py-3 px-3 transition-colors disabled:opacity-40 ${g.cls}`}
                >
                  <div className="text-[0.88rem] font-medium">{busy ? <Loader2 size={13} className="animate-spin mx-auto"/> : g.label}</div>
                  <div className="text-[0.68rem] text-neutral-500 mt-0.5">{g.hint}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Review;
